import { useCallback, type FC } from "react";
import { Controller, useWatch, type Control } from "react-hook-form";

import { useDetailsInlineSaving } from "@ds/components/details-view/hooks";
import { InputText } from "@ds/components/forms/inputs";
import { LabelWrapper } from "@ds/components/forms/labels";
import { isHtmlInputChangeEvent } from "@ds/utils/type-guards/form-guards";
import { isFormationPropertyRequired } from "@ds/utils/validation";

import { type FormationChangeHandlerDecorator, type FormationChangeHandlerInputData } from "./types";

export const StringFormationControl: FC<{
  control: Control<Experience | Project>;
  name: `formation.properties.${string}` | `template.experience_formation.properties.${string}`;
  property: TemplateStringFormationProperty;
  autoFocus?: boolean;
  excludeLabel?: boolean;
  onBeforeChangeHandlerDecorator: FormationChangeHandlerDecorator;
  onBlur?: () => void;
}> = ({ control, name, property, autoFocus, excludeLabel, onBeforeChangeHandlerDecorator, onBlur }) => {
  const onBlurHandler = useDetailsInlineSaving(onBlur);
  const propertyValue = (useWatch({ name, control }) as EntityStringFormationProperty)?.value;
  const onChangeHandler = useCallback(
    (onChange: (inputData: FormationChangeHandlerInputData) => void) =>
      onBeforeChangeHandlerDecorator(onChange, propertyValue ?? "", inputData =>
        isHtmlInputChangeEvent(inputData) ? inputData.target.value : "",
      ),
    [propertyValue, onBeforeChangeHandlerDecorator],
  );

  return (
    <Controller
      name={`${name}.value`}
      control={control}
      render={({ field: { ref, value, onChange, onBlur: onBlurFn, ...restFields }, fieldState }) => {
        const element = (
          <InputText
            inputRef={ref}
            value={value?.toString() || ""}
            autoFocus={autoFocus}
            onChange={onChangeHandler(onChange)}
            onBlur={onBlurHandler(onBlurFn)}
            {...restFields}
          />
        );

        if (excludeLabel) {
          return element;
        }

        return (
          <LabelWrapper
            label={property.title}
            className="col-8"
            isRequired={isFormationPropertyRequired(property.metadata?.validation_rules)}
            error={fieldState.error}
          >
            {element}
          </LabelWrapper>
        );
      }}
    />
  );
};
